// Client-side loader for Stripe.js (embedded Checkout).
//
// Stripe.js MUST be served from js.stripe.com (PCI requirement — you can't
// self-host or bundle it), so we inject the script tag directly rather than add
// `@stripe/stripe-js` as a dependency. Returns a Stripe instance for the given
// publishable key, loading the script at most once.

const STRIPE_JS_SRC = 'https://js.stripe.com/v3/';

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('Stripe.js can only load in the browser'));
  if ((window as { Stripe?: unknown }).Stripe) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${STRIPE_JS_SRC}"]`);
    const el = existing ?? document.createElement('script');
    el.addEventListener('load', () => resolve());
    el.addEventListener('error', () => {
      scriptPromise = null;
      reject(new Error('Failed to load Stripe.js (blocked by CSP or network?)'));
    });
    if (!existing) {
      el.src = STRIPE_JS_SRC;
      el.async = true;
      document.head.appendChild(el);
    } else if ((window as { Stripe?: unknown }).Stripe) {
      resolve();
    }
  });
  return scriptPromise;
}

/** Minimal shape of the embedded-checkout bits we use. */
export interface EmbeddedCheckout {
  mount(target: HTMLElement | string): void;
  destroy(): void;
}

/**
 * Result of `stripe.handleNextAction` — the browser half of an SCA/3DS challenge. Exactly one of
 * the two fields is set: `error` when the challenge was not completed (declined, abandoned, or a
 * transport failure — `type`/`code` tell those apart), `paymentIntent` with its post-challenge
 * `status` otherwise. Only the bits lib/upgrade-confirmation classifies are typed.
 *
 * An `error` may also carry the `payment_intent` it failed on. Its `status` is the authoritative
 * record of the outcome, so it is typed here too: a real failed 3DS arrives with a `type` the
 * verdict ladder does not recognize, and the intent's status is what disambiguates it.
 */
export interface HandleNextActionResult {
  error?: { type?: string; code?: string; message?: string; payment_intent?: { status?: string } };
  paymentIntent?: { status?: string };
}

export interface StripeInstance {
  initEmbeddedCheckout(opts: { clientSecret: string }): Promise<EmbeddedCheckout>;
  handleNextAction(opts: { clientSecret: string }): Promise<HandleNextActionResult>;
  // Side-effect-free read of the intent behind a client secret. Used to settle a challenge whose
  // own return value was inconclusive; shares HandleNextActionResult's shape.
  retrievePaymentIntent(clientSecret: string): Promise<HandleNextActionResult>;
}

export async function getStripe(publishableKey: string): Promise<StripeInstance> {
  await loadScript();
  const factory = (window as unknown as { Stripe?: (key: string) => StripeInstance }).Stripe;
  if (!factory) throw new Error('Stripe.js loaded but window.Stripe is missing');
  return factory(publishableKey);
}
